import { initialAssetDraft } from "./listNewAssetDraft";
import { isRealEstateCategory } from "../../../utils/assetCategory";

function idOfLoc(node) {
  if (!node) return "";
  if (typeof node === "string") return node.trim();
  return String(node._id || node.id || "").trim();
}

function nameOfLoc(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  return String(node.name || "").trim();
}

function labelToStructureKey(label, spvStructureTypeOptions) {
  const opts = spvStructureTypeOptions && typeof spvStructureTypeOptions === "object" ? spvStructureTypeOptions : {};
  const desired = String(label || "").trim().toLowerCase();
  if (!desired) return "";
  for (const [k, v] of Object.entries(opts)) {
    if (String(v).trim().toLowerCase() === desired) return k;
  }
  for (const [k, v] of Object.entries(opts)) {
    const vl = String(v).trim().toLowerCase();
    if (vl && (desired.includes(vl.slice(0, 14)) || vl.includes(desired.slice(0, 14)))) return k;
  }
  return "";
}

function labelToSizeUnitLabelFromApi(label, sizeUnitOptions) {
  const opts = sizeUnitOptions && typeof sizeUnitOptions === "object" ? sizeUnitOptions : {};
  const d = String(label || "").trim().toLowerCase();
  if (!d) return "sq ft";
  for (const [, v] of Object.entries(opts)) {
    if (String(v).trim().toLowerCase() === d) return String(v).trim();
  }
  return String(label).trim();
}

function normFeatureList(features, featureOptions) {
  const opts = Array.isArray(featureOptions) ? featureOptions : [];
  const out = [];
  for (const f of features || []) {
    const s = String(f || "").trim();
    if (!s) continue;
    const low = s.toLowerCase();
    const exact = opts.find((o) => o.toLowerCase() === low);
    if (exact) {
      out.push(exact);
      continue;
    }
    const close = opts.find(
      (o) => low.includes(o.toLowerCase()) || o.toLowerCase().includes(low)
    );
    out.push(close || s);
  }
  return [...new Set(out)];
}

function docUrlByHints(documents, hints) {
  const docs = Array.isArray(documents) ? documents : [];
  const norm = (s) => String(s || "").toLowerCase().replace(/[_\s-]+/g, "");
  const lowHints = hints.map((h) => norm(h));
  for (const d of docs) {
    const t = norm(d?.type);
    if (lowHints.length && lowHints.every((h) => t.includes(h))) {
      if (d?.fileUrl) return String(d.fileUrl).trim();
    }
  }
  for (const d of docs) {
    const t = norm(d?.type);
    if (lowHints.some((h) => t.includes(h))) {
      if (d?.fileUrl) return String(d.fileUrl).trim();
    }
  }
  return "";
}

function collectSpvDocUrls(documents) {
  const docs = Array.isArray(documents) ? documents : [];
  return docs
    .filter((d) => {
      const t = String(d?.type || "").toLowerCase();
      return (
        (t.includes("spv") || t.includes("certificate")) &&
        !t.includes("rega") &&
        !t.includes("valuation") &&
        !t.includes("legal") &&
        !t.includes("title") &&
        !t.includes("deed")
      );
    })
    .map((d) => String(d.fileUrl || "").trim())
    .filter(Boolean);
}

function blockchainLabelToDraftKey(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("polygon")) return "polygon";
  if (s.includes("xrpl") || s.includes("ripple")) return "xrpl";
  if (s.includes("ethereum")) return "ethereum";
  return "ethereum";
}

/**
 * Maps GET /api/owner/asset/:id payload into the list-new-asset wizard draft.
 * @param {object} asset — e.g. `res.data.data` from owner asset detail
 * @param {object} creationData — `mergeAssetCreationData(...)` (options maps + feature labels)
 */
export function mapOwnerApiAssetToDraft(asset, creationData) {
  const base = initialAssetDraft();
  if (!asset || typeof asset !== "object") return base;

  const cd = creationData && typeof creationData === "object" ? creationData : {};
  const basic = asset.basicInfo || {};
  const addr = basic.completeAddress || {};
  const specs = basic.propertySpecifications || {};
  const spv = asset.spvDetails || {};
  const fin = asset.financialDetails || {};
  const tok = asset.tokenEconomics || {};
  const tconf = asset.tokenConfiguration || {};
  const rega = asset.regaDetails || {};
  const docPack = asset.documentation || {};
  const documents = asset.documents;

  const countryId = idOfLoc(addr.country);
  const stateId = idOfLoc(addr.state);
  const cityId = idOfLoc(addr.city);

  const categoryId =
    idOfLoc(asset.assetCategory) || String(asset.assetCategory?._id || "").trim();
  const categoryTitle =
    nameOfLoc(asset.assetCategory) || String(asset.assetCategory?.name || "").trim();

  const isRE = isRealEstateCategory(asset);
  const commodity = basic.commodityAssetDetails || {};
  const luxury = basic.luxuryAssetDetails || {};

  const sizeUnitLabel =
    specs.sizeUnit != null && String(specs.sizeUnit).trim() !== ""
      ? String(specs.sizeUnit).trim()
      : base.sizeUnit;
  const sizeUnitForDraft = labelToSizeUnitLabelFromApi(sizeUnitLabel, cd.sizeUnitOptions);

  const features = Array.isArray(basic.featuresAndAmenities) ? basic.featuresAndAmenities : [];
  const amenityLabels = isRE ? normFeatureList(features, cd.featuresAndAmenitiesOptions) : [];

  const structureKey = labelToStructureKey(spv.structureType, cd.spvStructureTypeOptions);

  const titleDeedUrl =
    docPack.titleDeed?.fileUrl || docUrlByHints(documents, ["title", "deed"]) || "";
  const legalUrl =
    (Array.isArray(docPack.legalDocuments) && docPack.legalDocuments[0]?.fileUrl) ||
    docUrlByHints(documents, ["legal"]) ||
    "";

  const valuationUrl =
    fin.valuationReport?.fileUrl || docUrlByHints(documents, ["valuation"]) || "";

  const regaCertUrl =
    docPack.regaCertificate?.fileUrl ||
    docUrlByHints(documents, ["rega", "certificate"]) ||
    docUrlByHints(documents, ["regacertificate"]) ||
    "";

  const spvSigUrl =
    docUrlByHints(documents, ["signatory"]) ||
    docUrlByHints(documents, ["spv", "sign"]) ||
    docPack.spvSignature?.fileUrl ||
    "";

  const spvDocUrls = collectSpvDocUrls(documents);

  const minInv = fin.minimumInvestment != null ? String(fin.minimumInvestment) : "";
  const tokenPrice =
    tok.tokenPriceUSD != null && String(tok.tokenPriceUSD).trim() !== ""
      ? String(tok.tokenPriceUSD)
      : base.pricePerTokenSar;

  const _editMeta = Object.fromEntries(
    Object.entries({
      regaCertificateUrl: regaCertUrl,
      spvSignatureUrl: spvSigUrl,
      valuationReportUrl: valuationUrl,
      titleDeedUrl,
      legalDocumentUrl: legalUrl,
      mainImageUrl: asset.assetImages?.mainImage?.fileUrl,
      spvDocumentUrls: spvDocUrls.length ? spvDocUrls : undefined,
    }).filter(([, v]) => v != null && String(v).trim() !== "")
  );

  const renewablePatch =
    !isRE && categoryTitle === "Renewable Energy"
      ? {
          energyType: String(specs.energyType || "").trim(),
          installedCapacityValue:
            specs.installedCapacity != null ? String(specs.installedCapacity) : "",
          installedCapacityUnit: String(specs.installedCapacityUnit || "MW").trim() || "MW",
          annualProductionMwh:
            specs.annualProductionMWh != null ? String(specs.annualProductionMWh) : "",
          projectLocationSite: String(
            specs.projectLocationSite || addr.streetAddress || ""
          ).trim(),
        }
      : {};

  const commodityPatch =
    categoryTitle === "Commodities"
      ? {
          commodityType: String(commodity.commodityType || "").trim(),
          commodityQuantityValue:
            commodity.commodityQuantity != null ? String(commodity.commodityQuantity) : "",
          commodityQuantityUnit:
            String(commodity.commodityQuantityUnit || "kg").trim() || "kg",
          purityOrGrade: String(commodity.grade || "").trim(),
          storageLocationVault: String(commodity.location || "").trim(),
        }
      : {};

  const luxuryPatch =
    categoryTitle === "Luxury Goods"
      ? {
          luxuryBrand: String(luxury.brand || "").trim(),
          luxuryModel: String(luxury.model || "").trim(),
          luxurySerialNumber: String(luxury.serialNumber || "").trim(),
          luxuryYearOfManufacture: String(luxury.manufacturedYear || "").trim(),
          luxuryCondition: String(luxury.condition || "").trim(),
        }
      : {};

  const realEstatePatch = isRE
    ? {
        propertyType: String(specs.propertyType || "").trim(),
        sizeValue: specs.sizeSqFt != null ? String(specs.sizeSqFt) : "",
        sizeUnit: sizeUnitForDraft,
        bedrooms: specs.bedrooms != null ? String(specs.bedrooms) : "",
        bathrooms: specs.bathrooms != null ? String(specs.bathrooms) : "",
        parkingSpaces: specs.parkingSpaces != null ? String(specs.parkingSpaces) : "",
        yearBuilt: specs.yearBuilt != null ? String(specs.yearBuilt) : "",
        amenityIds: amenityLabels,
        regaDeedNumber: String(rega.deedNumber || "").trim(),
        regaCrNumber: String(rega.crNumber || "").trim(),
        regaOwnerName: String(rega.ownerName || "").trim(),
      }
    : {};

  return {
    ...base,
    categoryId,
    categoryTitle,
    assetName: String(basic.assetName || "").trim(),
    description: String(basic.description || "").trim(),
    streetAddress: String(addr.streetAddress || "").trim(),
    addressCountryId: countryId,
    addressStateId: stateId,
    addressCityId: cityId,
    country: nameOfLoc(addr.country) || countryId,
    state: nameOfLoc(addr.state) || stateId,
    city: nameOfLoc(addr.city) || cityId,
    zipCode: String(addr.zipCode || "").trim(),
    ...realEstatePatch,
    ...renewablePatch,
    ...commodityPatch,
    ...luxuryPatch,
    spvName: String(spv.spvName || "").trim(),
    registrationNumber: String(spv.registrationNumber || "").trim(),
    jurisdiction: String(spv.jurisdiction || "").trim(),
    structureType: structureKey || String(spv.structureType || "").trim(),
    formationDate: String(spv.formationDate || "").slice(0, 10),
    registeredAddress: String(spv.registeredAddress || "").trim(),
    trn: String(spv.taxRegistrationNumber || "").trim(),
    licenseExpiryDate: String(spv.licenseExpiryDate || "").slice(0, 10),
    purpose: String(spv.purpose || "").trim(),
    totalAssetValue: fin.totalAssetValue != null ? String(fin.totalAssetValue) : "",
    expectedRoi: fin.expectedROI != null ? String(fin.expectedROI) : "",
    rentalYield: fin.rentalYield != null ? String(fin.rentalYield) : "",
    dividendFrequency: String(fin.dividendFrequency || "").trim(),
    minimumInvestmentSar: minInv,
    pricePerTokenSar: tokenPrice,
    blockchainNetwork: blockchainLabelToDraftKey(tconf.blockchainNetwork),
    fractionalOwnership: tconf.fractionalOwnership !== false,
    _editMeta,
  };
}
